/**
 * RFC 0036 + RFC 0392: Agent chat synchronization hook.
 *
 * Manages conversation with the backend orchestrator via:
 * - WebSocket /ws/chat/{projectId} for real-time events (state, messages, chunks)
 * - POST /agent/connect to start a conversation (returns JSON)
 * - POST /agent/respond to answer questions (returns JSON)
 *
 * The backend runs agents as background tasks. Real-time events flow
 * through the persistent WebSocket connection.
 */

import { useState, useCallback, useMemo } from 'react'
import { apiFetch, api, createDebug, createNadDebug } from '@ponpon/api-client'
import {
  useChatSocket,
  type ChatEvent,
  type ChatEventMessage,
  type ChatEventQuestion,
  type ChatEventQuestionMetadata,
} from './useChatSocket'
const log = createDebug('dw:chat:agent')
const eventLog = createDebug('dw:chat:event')
const thinkingLog = createDebug('dw:chat:thinking')
const dedupLog = createNadDebug('dw:chat:dedup')
const questionLog = createNadDebug('dw:chat:question')
const questionSubmitLog = createNadDebug('dw:chat:question-submit')
const editResendLog = createNadDebug('dw:chat:edit-resend')

type ConversationStatus = 'idle' | 'running' | 'pending_questions' | 'error'

/** Answer value sent to the API: always has a selected option, optionally custom text */
export interface AnswerValue {
  selected_option: string
  custom_text?: string
}

const SCHEDULING_QUESTION_PREFIXES: readonly string[] = [
  '__cost_confirmation__',
  '__insufficient_funds__',
]

function isSchedulingQuestionKey(key: string): boolean {
  return SCHEDULING_QUESTION_PREFIXES.some((prefix) => key.startsWith(prefix))
}

function selectedOptionsSummary(answers: Record<string, AnswerValue>): string {
  return Object.entries(answers)
    .map(([key, answer]) => `${key}:${answer.selected_option}`)
    .join(',')
}

function customTextLengthsSummary(answers: Record<string, AnswerValue>): string {
  return Object.entries(answers)
    .filter(([, answer]) => Boolean(answer.custom_text))
    .map(([key, answer]) => `${key}:${answer.custom_text?.length ?? 0}`)
    .join(',')
}

function tracePreview(content: string): string {
  const compact = content.replace(/\s+/g, ' ').trim()
  return compact.length > 80 ? `${compact.slice(0, 77)}...` : compact
}

function traceRoles(messages: ChatMessage[]): string {
  return messages.map((message) => message.role).join(',')
}

function createEditTraceId(projectId: string, timestamp: string): string {
  const slug = `${projectId}-${timestamp}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `chat-edit-${slug}`
}

/** Question embedded in a message (new unified model) */
export interface EmbeddedQuestion {
  question_type: string
  tool_use_id: string
  question: string
  options: Array<{ value?: string; label: string; description?: string }>
  allow_custom_input?: boolean
  header?: string
  answer?: string | null
  metadata?: ChatEventQuestionMetadata // Extra data (e.g., node details for cost confirmation)
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  agent?: string
  content: string
  referenced_node_ids?: string[]
  timestamp: string
  questions?: EmbeddedQuestion[] // Optional embedded questions
  /** True while this message is actively being streamed from the backend */
  isStreaming?: boolean
  /** True if this message was delivered via real streaming (not history) */
  wasStreamed?: boolean
  /** True for ephemeral system notifications (e.g. context compaction) */
  isSystemNotification?: boolean
}

function parseEmbeddedQuestion(value: ChatEventQuestion | undefined): EmbeddedQuestion | undefined {
  if (!value) return undefined
  const rawOptions = value.options ?? []
  const options = rawOptions.map((option) => ({
    value: option.value,
    label: option.label ?? option.value ?? '',
    description: option.description,
  }))
  const answerValue = value.answer
  const answer = typeof answerValue === 'string' || answerValue === null ? answerValue : undefined

  return {
    question_type: value.question_type ?? '',
    tool_use_id: value.tool_use_id ?? '',
    question: value.question ?? '',
    options,
    allow_custom_input: value.allow_custom_input,
    header: value.header,
    answer,
    metadata: value.metadata,
  }
}

function parseChatMessage(value: ChatEventMessage | undefined): ChatMessage | undefined {
  if (!value) return undefined
  const role = value.role
  if (role !== 'user' && role !== 'assistant') return undefined
  const timestamp = value.timestamp ?? new Date().toISOString()
  const id = value.id ?? `${role}-${timestamp}`
  const questions = value.questions?.map(parseEmbeddedQuestion).filter((q): q is EmbeddedQuestion => !!q)

  return {
    id,
    role,
    agent: value.agent,
    content: value.content ?? '',
    referenced_node_ids: value.referenced_node_ids,
    timestamp,
    questions,
    isStreaming: value.isStreaming,
    wasStreamed: value.wasStreamed,
    isSystemNotification: value.isSystemNotification,
  }
}

function parseChatMessages(value: ChatEventMessage[] | undefined): ChatMessage[] {
  if (!Array.isArray(value)) return []
  return value.map(parseChatMessage).filter((message): message is ChatMessage => !!message)
}

function conversationStatus(value: string | undefined, fallback: ConversationStatus): ConversationStatus {
  return value === 'idle' || value === 'running' || value === 'pending_questions' || value === 'error'
    ? value
    : fallback
}

export function useAgentChat(projectId: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [status, setStatus] = useState<ConversationStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRestoredSession, setIsRestoredSession] = useState(true)
  // RFC-0289: True between calling startWithPrompt and receiving the first
  // streaming chunk.  Used by AgentChat to show "Thinking..." correctly for
  // ALL paths that invoke the Director (send, edited prompt resend, queued prompts).
  const [awaitingDirector, setAwaitingDirector] = useState(false)

  const firstUnansweredMessageIndex = useMemo(() => {
    const _isSchedulingId = (id: string) =>
      id.startsWith('__cost_confirmation__') || id.startsWith('__insufficient_funds__')
    const allQuestionMsgs: Array<{idx: number, qCount: number, unanswered: number, toolIds: string[]}> = []
    for (let i = 0; i < messages.length; i++) {
      const questions = messages[i].questions
      if (questions?.length) {
        const unanswered = questions.filter(q => !q.answer && !_isSchedulingId(q.tool_use_id))
        allQuestionMsgs.push({
          idx: i,
          qCount: questions.length,
          unanswered: unanswered.length,
          toolIds: questions.map(q => q.tool_use_id),
        })
        if (unanswered.length > 0) {
          questionLog({
            event: 'first-unanswered-found',
            messageIndex: i,
            totalMessages: messages.length,
            questionCount: questions.length,
            unansweredCount: unanswered.length,
            toolIds: questions.map(q => q.tool_use_id).join(','),
            answers: questions.map(q => q.answer ?? 'null').join(','),
          })
          return i
        }
      }
    }
    if (allQuestionMsgs.length > 0) {
      questionLog({
        event: 'no-unanswered-questions',
        totalMessages: messages.length,
        questionMessages: allQuestionMsgs.length,
        details: JSON.stringify(allQuestionMsgs),
      })
    }
    return -1
  }, [messages])

  // Handle incoming WebSocket events
  const handleChatEvent = useCallback((event: ChatEvent) => {
    log('event type=%s', event.type)
    const eventMessages = parseChatMessages(event.messages)
    eventLog('received type=%s status=%s msgCount=%s', event.type, event.status ?? '-', eventMessages.length || '-')
    switch (event.type) {
      case 'state': {
        const incoming = eventMessages
        thinkingLog('state event status=%s msgCount=%d', event.status, incoming.length)
        eventLog('state status=%s incomingMsgCount=%d', event.status, incoming.length)

        // NAD: trace question cards in incoming state
        const incomingQuestionMsgs = incoming.filter(m => m.questions?.length)
        if (incomingQuestionMsgs.length > 0) {
          for (const m of incomingQuestionMsgs) {
            questionLog({
              event: 'state-incoming-questions',
              status: typeof event.status === 'string' ? event.status : '',
              messageRole: m.role,
              messageContent: m.content?.slice(0, 80),
              questionCount: m.questions!.length,
              toolIds: m.questions!.map(q => q.tool_use_id).join(','),
              answers: m.questions!.map(q => q.answer ?? 'null').join(','),
              allSkipped: m.questions!.every(q => q.answer === '__skipped__'),
            })
          }
        } else {
          questionLog({
            event: 'state-no-questions',
            status: typeof event.status === 'string' ? event.status : '',
            incomingCount: incoming.length,
          })
        }

        setMessages((prev) => {
          // Preserve frontend-only `wasStreamed` flag so the Typewriter
          // doesn't re-animate messages that were already streamed live.
          const streamedTs = new Set(
            prev.filter((m) => m.wasStreamed).map((m) => m.timestamp),
          )

          // RFC-0289: Preserve optimistic user messages not yet in the
          // server's history.  When the user sends a message while workers
          // are running, the backend may broadcast a state event (from node
          // completion) before the user's POST is processed.  Without this,
          // the optimistic message is silently wiped.
          //
          // Content-based dedup: also filter out optimistic messages whose
          // content already appears in the incoming server messages. This
          // handles the common case where client and server timestamps differ
          // (client generates its own timestamp, server generates another),
          // which would otherwise cause the optimistic to always be preserved
          // alongside the server's copy — resulting in a duplicate.
          //
          // Count-aware: if the user sent the same text N times (e.g. "yes")
          // and the server has M copies, preserve max(0, N - M) optimistic
          // copies so repeated prompts aren't incorrectly dropped.
          const incomingTs = new Set(incoming.map((m) => m.timestamp))

          // Count how many times each content appears in incoming user messages
          const incomingContentCounts = new Map<string, number>()
          for (const m of incoming) {
            if (m.role === 'user') {
              incomingContentCounts.set(m.content, (incomingContentCounts.get(m.content) ?? 0) + 1)
            }
          }

          // Track how many optimistic messages we've matched per content
          const matchedCounts = new Map<string, number>()
          const optimistic = prev.filter((m) => {
            if (m.role !== 'user' || !m.id?.startsWith('user-')) return false
            // Keep if timestamp already matches (server has this exact message)
            if (incomingTs.has(m.timestamp)) return false

            // Content-based dedup: check if this content exists in incoming
            const serverCount = incomingContentCounts.get(m.content) ?? 0
            if (serverCount > 0) {
              const alreadyMatched = matchedCounts.get(m.content) ?? 0
              if (alreadyMatched < serverCount) {
                // This optimistic is accounted for by a server copy — drop it
                matchedCounts.set(m.content, alreadyMatched + 1)
                return false
              }
              // More optimistic copies than server copies — preserve the excess
            }
            return true
          })

          dedupLog({
            event: 'state-dedup',
            prev: prev.length,
            incoming: incoming.length,
            optimistic: optimistic.length,
            incomingTsCount: incomingTs.size,
            prevUserCount: prev.filter((m) => m.role === 'user').length,
          })

          // Preserve messages that are actively being streamed. During
          // streaming, the message isn't in the server's conversation
          // history yet, so a state event (e.g. from a node completing)
          // would wipe it.  When chat_stream_end + chat_message arrive
          // later, the streaming message is replaced by the final version.
          const streaming = prev.filter((m) => m.isStreaming)

          let merged = streamedTs.size === 0
            ? incoming
            : incoming.map((m) =>
                streamedTs.has(m.timestamp) ? { ...m, wasStreamed: true } : m,
              )

          // Append optimistic messages that the server doesn't know about yet
          if (optimistic.length > 0) {
            merged = [...merged, ...optimistic]
          }

          // Append actively streaming messages so they aren't wiped
          if (streaming.length > 0) {
            merged = [...merged, ...streaming]
          }

          return merged
        })
        const eventStatus = conversationStatus(event.status, 'idle')
        setStatus(eventStatus)
        if (eventStatus !== 'error') {
          setError(null)
        }
        setIsLoading(false)
        break
      }

      case 'workflow':
        if (event.error) {
          setError(`Canvas error: ${event.error}`)
        }
        break

      case 'chat_message':
        setIsRestoredSession(false)
        {
          const chatMsg = parseChatMessage(event)
          if (chatMsg?.questions?.length) {
            questionLog({
              event: 'chat-message-with-questions',
              role: chatMsg.role,
              questionCount: chatMsg.questions.length,
              toolIds: chatMsg.questions.map(q => q.tool_use_id).join(','),
              answers: chatMsg.questions.map(q => q.answer ?? 'null').join(','),
            })
          }
        }
        setMessages((prev) => {
          const chatMsg = parseChatMessage(event)
          if (!chatMsg) return prev
          if (chatMsg.role === 'user') {
            // Primary: backward scan for adjacent optimistic user message
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (
                m.role === 'user' &&
                m.id &&
                (m.id.startsWith('optimistic-answer-') || m.id.startsWith('user-'))
              ) {
                const updated = [...prev]
                updated[i] = chatMsg
                return updated
              }
              if (m.role !== 'user') break
            }
            // Fallback: scan ALL messages for any optimistic with matching
            // content. This handles the case where an assistant message (e.g.
            // streaming chunk) arrived between the optimistic and the end of
            // the array, causing the backward scan to break early.
            const eventContent = chatMsg.content
            const optimisticIdx = prev.findIndex(
              (m) =>
                m.role === 'user' &&
                m.id?.startsWith('user-') &&
                m.content === eventContent,
            )
            if (optimisticIdx >= 0) {
              dedupLog({ event: 'chat-message-fallback', optimisticIdx: optimisticIdx })
              const updated = [...prev]
              updated[optimisticIdx] = chatMsg
              return updated
            }
          }

          if (chatMsg.role === 'assistant' && (chatMsg.agent === 'director' || !chatMsg.agent)) {
            // First: match by server-stable id. ``chat_chunk`` events
            // create entries with ``id = event.message_id``; the final
            // ``chat_message`` event has the SAME server id under
            // ``value.id``. Without this match the chunked partial
            // stays in the array and the final message gets appended
            // as a second entry. On a brief SSE reconnect (e.g. mid-
            // deploy SIGTERM) the final replays AGAIN, producing the
            // 3× duplicate the user reported. (Issue #11.)
            if (chatMsg.id) {
              const sameIdIdx = prev.findIndex((m) => m.id === chatMsg.id)
              if (sameIdIdx >= 0) {
                const updated = [...prev]
                updated[sameIdIdx] = {
                  ...chatMsg,
                  wasStreamed: prev[sameIdIdx].wasStreamed,
                }
                return updated
              }
            }
            // Legacy fallback — match the most-recent streamed entry
            // that has no stable server id. Stays for backward compat
            // with older servers that don't echo message_id back.
            for (let i = prev.length - 1; i >= 0; i--) {
              const m = prev[i]
              if (m.role === 'assistant' && m.wasStreamed && m.id?.startsWith('stream-')) {
                const updated = [...prev]
                updated[i] = { ...chatMsg, wasStreamed: true }
                return updated
              }
              if (m.role !== 'assistant') break
            }
          }

          const existingIndex = prev.findIndex((m) => m.timestamp === chatMsg.timestamp)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = chatMsg
            return updated
          }
          return [...prev, chatMsg]
        })
        break

      case 'chat_chunk':
        setIsRestoredSession(false)
        {
          const chunkMessageId = typeof event.message_id === 'string' ? event.message_id : ''
          const chunkContent = typeof event.content === 'string' ? event.content : ''
          log('chunk msgId=%s len=%d', chunkMessageId, chunkContent.length)
        }
        setMessages((prev) => {
          const msgId = typeof event.message_id === 'string' ? event.message_id : ''
          const chunk = typeof event.content === 'string' ? event.content : ''
          if (!msgId) return prev
          const existingIndex = prev.findIndex((m) => m.id === msgId)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              content: updated[existingIndex].content + chunk,
              // One stream id now spans ALL LLM iterations of a turn
              // (2026-07-07 duplicated-bubble fix): iteration N>1's chunks
              // arrive AFTER iteration N-1's chat_stream_end flipped
              // isStreaming=false. Re-assert streaming on append so mid-turn
              // `state` broadcasts (which only preserve isStreaming partials)
              // can't wipe the live bubble between iterations.
              isStreaming: true,
            }
            return updated
          }
          return [
            ...prev,
            {
              id: msgId,
              role: 'assistant',
              agent: 'director',
              content: chunk,
              timestamp: new Date().toISOString(),
              isStreaming: true,
              wasStreamed: true,
            },
          ]
        })
        break

      case 'chat_stream_end':
        log('stream_end msgId=%s has_tool_use=%s', event.message_id, event.has_tool_use)
        thinkingLog('chat_stream_end -> isStreaming=false for msgId=%s has_tool_use=%s',
          event.message_id, event.has_tool_use)
        setMessages((prev) => {
          const msgId = typeof event.message_id === 'string' ? event.message_id : ''
          if (!msgId) return prev
          const existingIndex = prev.findIndex((m) => m.id === msgId)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = {
              ...updated[existingIndex],
              isStreaming: false,
            }
            return updated
          }
          return prev
        })
        // Only clear awaitingDirector when the LLM round has no tool_use,
        // meaning this is the final round. If has_tool_use is true, the
        // Director will execute tools and make another LLM call, so keep
        // awaitingDirector=true to prevent the banner from flickering off.
        if (event.has_tool_use !== true) {
          thinkingLog('chat_stream_end -> no tool_use, clearing awaitingDirector')
          setAwaitingDirector(false)
        } else {
          thinkingLog('chat_stream_end -> has tool_use, keeping awaitingDirector=true')
        }
        break

      case 'status': {
        const newStatus = conversationStatus(event.status, status)
        thinkingLog('status event newStatus=%s (prev=%s)', newStatus, status)
        eventLog('status-change old=%s new=%s', status, newStatus)
        setStatus(newStatus)
        break
      }

      case 'done': {
        const doneStatus = conversationStatus(event.status, 'idle')
        thinkingLog('done event doneStatus=%s -> awaitingDirector=false', doneStatus)
        eventLog('done status=%s', doneStatus)
        setStatus(doneStatus)
        setAwaitingDirector(false)
        break
      }

      case 'error':
        thinkingLog('error event -> awaitingDirector=false, msg=%s', event.message)
        eventLog('error msg=%s', event.message)
        setError(typeof event.message === 'string' ? event.message : 'Unknown error')
        setStatus('error')
        setAwaitingDirector(false)
        break

      case 'cancelled':
        thinkingLog('cancelled event -> awaitingDirector=false')
        eventLog('cancelled')
        setStatus('idle')
        setAwaitingDirector(false)
        setMessages((prev) => [
          ...prev,
          {
            id: `cancelled-${Date.now()}`,
            role: 'assistant',
            content: typeof event.message === 'string' ? event.message : 'Execution stopped.',
            timestamp: new Date().toISOString(),
          },
        ])
        break

      case 'context_compacted':
        setMessages((prev) => [
          ...prev,
          {
            id: `compacted-${Date.now()}`,
            role: 'assistant',
            content: 'Compacting conversation history\u2026',
            timestamp: new Date().toISOString(),
            isSystemNotification: true,
          },
        ])
        break

      case 'node_status':
        // Cross-instance node status sync: when the node ran on a different
        // API instance, the YDoc write was local to that instance.  This
        // event arrives via Redis Pub/Sub so we can update the local YDoc.
        // Dispatch a DOM event so useYCanvas can apply it without prop threading.
        if (typeof event.node_id === 'string' && typeof event.status === 'string') {
          const hasError = Object.prototype.hasOwnProperty.call(event, 'error')
          const error =
            typeof event.error === 'string'
              ? event.error
              : event.error === null
                ? null
                : undefined
          window.dispatchEvent(
            new CustomEvent('dw:node-status', {
              detail: {
                nodeId: event.node_id,
                status: event.status,
                ...(hasError ? { error } : {}),
              },
            }),
          )
        }
        break
    }
  }, [status])

  // Connect WebSocket for real-time events
  useChatSocket({
    projectId,
    enabled: !!projectId,
    onEvent: handleChatEvent,
  })

  // Start a new conversation with a prompt (via POST /agent/connect)
  const startWithPrompt = useCallback(
    async (prompt: string, referencedNodeIds?: string[], partialAnswers?: Record<string, string>) => {
      // RFC-0289: Backend accepts prompts during "running" state (workers
      // in-progress) so we no longer block here.  The Director lock on the
      // backend serialises concurrent invocations.
      if (!projectId) return

      // Show user's prompt in chat immediately (optimistic)
      if (prompt.trim()) {
        setMessages((prev) => [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: 'user',
            content: prompt,
            referenced_node_ids:
              referencedNodeIds && referencedNodeIds.length > 0 ? referencedNodeIds : undefined,
            timestamp: new Date().toISOString(),
          },
        ])
      }

      setStatus('running')
      setAwaitingDirector(true)
      thinkingLog('startWithPrompt -> status=running, awaitingDirector=true')
      setError(null)

      try {
        const body: { prompt: string; referenced_node_ids?: string[]; partial_answers?: Record<string, string> } = {
          prompt,
        }
        if (referencedNodeIds && referencedNodeIds.length > 0) {
          body.referenced_node_ids = referencedNodeIds
        }
        if (partialAnswers && Object.keys(partialAnswers).length > 0) {
          body.partial_answers = partialAnswers
        }

        const response = await apiFetch(
          `/projects/${projectId}/agentic/agent/connect`,
          {
            method: 'POST',
            body: JSON.stringify(body),
          }
        )

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }

        // JSON response — events come via WebSocket.
        // Log if the backend didn't actually spawn the Director (prompt silently ignored).
        const data = await response.json()
        if (prompt.trim() && data && !data.ok) {
          console.warn('[useAgentChat] /agent/connect did not spawn Director:', data.status)
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.error('Failed to start agent:', err)
        setError(err instanceof Error ? err.message : 'Failed to start agent')
        setStatus('idle')
      }
    },
    [projectId]
  )

  // Respond to pending questions (via POST /agent/respond)
  const respondToQuestions = useCallback(
    async (answers: Record<string, AnswerValue>) => {
      if (!projectId) {
        return
      }

      const answerKeys = Object.keys(answers)
      const customTextLengths = customTextLengthsSummary(answers)
      const selectedOptions = selectedOptionsSummary(answers)
      const isDirectorAnswer = answerKeys.some(
        (key) => !isSchedulingQuestionKey(key),
      )
      questionSubmitLog({
        event: 'question-submit:request',
        projectId,
        answerCount: answerKeys.length,
        answerKeys: answerKeys.join(','),
        selectedOptions,
        customTextLengths,
        hasCustomText: Boolean(customTextLengths),
        schedulingOnly: answerKeys.length > 0 && !isDirectorAnswer,
        directorAnswer: isDirectorAnswer,
      })

      // Optimistically update question answers
      setMessages((prev) => {
        const updated = prev.map((msg) => {
          if (!msg.questions) return msg
          const updatedQuestions = msg.questions.map((q) => {
            const key = q.tool_use_id
            const answerVal = key ? answers[key] : undefined
            if (answerVal) {
              return { ...q, answer: answerVal.custom_text || answerVal.selected_option }
            }
            return q
          })
          if (updatedQuestions.some((q, i) => q !== msg.questions![i])) {
            return { ...msg, questions: updatedQuestions }
          }
          return msg
        })

        // Custom input still needs an optimistic user bubble
        if (answers['__custom_input__']?.custom_text) {
          updated.push({
            id: `user-${Date.now()}`,
            role: 'user',
            content: answers['__custom_input__'].custom_text,
            timestamp: new Date().toISOString(),
          })
        }
        return updated
      })

      setStatus('running')
      // Only set awaitingDirector when the answer resumes the Director
      // (i.e. not for scheduling-only questions like cost confirmation
      // or insufficient funds which only trigger worker nodes).
      if (isDirectorAnswer) {
        setAwaitingDirector(true)
        thinkingLog('respondToQuestions -> status=running, awaitingDirector=true (director answer)')
      } else {
        thinkingLog('respondToQuestions -> status=running (scheduling answer, awaitingDirector unchanged)')
      }
      setError(null)

      try {
        const response = await apiFetch(
          `/projects/${projectId}/agentic/agent/respond`,
          {
            method: 'POST',
            body: JSON.stringify({ answers }),
          }
        )

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }

        // JSON response — events come via WebSocket
        await response.json()
        questionSubmitLog({
          event: 'question-submit:response',
          projectId,
          answerCount: answerKeys.length,
          status: response.status,
        })
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        questionSubmitLog({
          event: 'question-submit:error',
          projectId,
          answerCount: answerKeys.length,
          error: err instanceof Error ? err.message : String(err),
        })
        console.error('Failed to respond to agent:', err)
        setError(err instanceof Error ? err.message : 'Failed to respond')
        setStatus('idle')
      }
    },
    [projectId]
  )

  // RFC 0061: Stop the running agent execution
  const stop = useCallback(async () => {
    if (!projectId || status !== 'running') return

    try {
      await api.cancelAgentExecution(projectId)
    } catch (err) {
      console.error('Failed to cancel agent:', err)
    }

    setStatus('idle')
  }, [projectId, status])

  // Delete a message by timestamp (optimistic removal + API call)
  const deleteMessage = useCallback(async (timestamp: string, content: string) => {
    if (!projectId || status === 'running') return

    setMessages((prev) => prev.filter((m) => m.timestamp !== timestamp))

    try {
      const response = await apiFetch(
        `/projects/${projectId}/agentic/agent/delete-message`,
        {
          method: 'POST',
          body: JSON.stringify({ timestamp, content }),
        }
      )
      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`)
      }
      const result = await response.json()
      if (!result.success) {
        console.warn('Delete message API returned:', result.reason)
      }
    } catch (err) {
      console.error('Failed to delete message:', err)
    }
  }, [projectId, status])

  // Edit a message in place, remove dependent later turns, then replay Director.
  const editMessage = useCallback(async (timestamp: string, originalContent: string, newContent: string, referencedNodeIds?: string[]) => {
    if (!projectId || status === 'running') return

    const previousStatus = status
    const targetIndex = messages.findIndex((m) => m.timestamp === timestamp)
    if (targetIndex < 0) return

    const previousMessages = messages
    const removedMessages = messages.slice(targetIndex + 1)
    const editTraceId = createEditTraceId(projectId, timestamp)
    const traceScenario = 'edit_second_user_message_resend'
    const editedMessage: ChatMessage = {
      ...messages[targetIndex],
      content: newContent,
      referenced_node_ids:
        referencedNodeIds && referencedNodeIds.length > 0 ? referencedNodeIds : undefined,
    }
    const nextMessages = [...messages.slice(0, targetIndex), editedMessage]
    setMessages(nextMessages)

    setStatus('running')
    setAwaitingDirector(true)
    setError(null)

    editResendLog({
      event: 'chat-edit:submit',
      trace_id: editTraceId,
      layer: 'client',
      scenario: traceScenario,
      project_id: projectId,
      target_timestamp: timestamp,
      previous_status: previousStatus,
      original_length: originalContent.length,
      edited_length: newContent.length,
      original_preview: tracePreview(originalContent),
      edited_preview: tracePreview(newContent),
      referenced_node_count: referencedNodeIds?.length ?? 0,
    })

    editResendLog({
      event: 'chat-edit:client_optimistic_rewind_applied',
      trace_id: editTraceId,
      layer: 'client',
      scenario: traceScenario,
      project_id: projectId,
      target_timestamp: timestamp,
      target_message_index: targetIndex,
      ui_message_count_before: previousMessages.length,
      ui_message_count_after: nextMessages.length,
      removed_message_count: removedMessages.length,
      retained_roles: traceRoles(nextMessages),
      removed_conversation_roles: traceRoles(removedMessages),
      status_before: previousStatus,
      status_after: 'running',
    })

    try {
      const body: {
        timestamp: string
        content: string
        original_content: string
        trace_id: string
        referenced_node_ids?: string[]
      } = {
        timestamp,
        content: newContent,
        original_content: originalContent,
        trace_id: editTraceId,
      }
      if (referencedNodeIds && referencedNodeIds.length > 0) {
        body.referenced_node_ids = referencedNodeIds
      }

      const response = await apiFetch(
        `/projects/${projectId}/agentic/agent/edit-message`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      )
      if (!response.ok) {
        throw new Error(`Edit failed: ${response.status}`)
      }
      const result = await response.json()
      if (!result.success) {
        throw new Error(result.reason ?? 'Edit failed')
      }
      editResendLog({
        event: 'chat-edit:server_edit_request_accepted',
        trace_id: editTraceId,
        layer: 'client',
        scenario: traceScenario,
        project_id: projectId,
        target_timestamp: timestamp,
        api_status: result.status ?? 'unknown',
        rewound_after_edit: result.rewound_after_edit === true,
      })
    } catch (err) {
      console.error('Failed to edit message:', err)
      editResendLog({
        event: 'chat-edit:client_restore_after_edit_failed',
        trace_id: editTraceId,
        layer: 'client',
        scenario: traceScenario,
        project_id: projectId,
        target_timestamp: timestamp,
        restored_message_count: previousMessages?.length ?? 0,
        restored_status: previousStatus,
        error_message: err instanceof Error ? err.message : 'Failed to edit message',
      })
      if (previousMessages) {
        setMessages(previousMessages)
      }
      setError(err instanceof Error ? err.message : 'Failed to edit message')
      setStatus(previousStatus)
      setAwaitingDirector(false)
    }
  }, [projectId, status, messages])

  const dismissPendingQuestions = useCallback((partialAnswers?: Record<string, string>) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (!msg.questions) return msg
        const updated = msg.questions.map((q) => {
          if (q.answer) return q
          const partial = partialAnswers?.[q.tool_use_id]
          if (partial) {
            return { ...q, answer: `User selected: ${partial}` }
          }
          return { ...q, answer: '__skipped__' }
        })
        if (updated.some((q, i) => q !== msg.questions![i])) {
          return { ...msg, questions: updated }
        }
        return msg
      }),
    )
  }, [])

  return {
    messages,
    firstUnansweredMessageIndex,
    status,
    error,
    isLoading,
    isRestoredSession,
    awaitingDirector,
    startWithPrompt,
    respondToQuestions,
    dismissPendingQuestions,
    deleteMessage,
    editMessage,
    stop,
  }
}
